import React, { useEffect, useRef, useState } from 'react';
import ActionMessage from '../../../components/ActionMessage/ActionMessage';
import SelectSymbol from '../../../components/SelectSymbol/SelectSymbol';
import SwitchInput from '../../../components/SwitchInput/SwitchInput';
import { saveGrid } from '../../../services/AutomationsService';
import { processError } from '../../../services/ServiceUtils';
import { getMemoryIndex } from '../../../services/BeholderService';
import WalletSummary from '../../../components/WalletSummary/WalletSummary';
import SymbolPrice from '../../../components/SymbolPrice/SymbolPrice';
import '../Automations.css';
import { getSymbol } from '../../../services/SymbolsService';
import GridTable from './GridTable';
import LogButton from '../../../components/Logs/LogButton';
import LogView from '../../../components/Logs/LogView';
import GridButton from './GridButton';
import GridLineChart from './GridLineChart';
import { quantityTypeNames, quantityTypes } from '../../../utils/QuantityUtils';
import { actionTypes } from '../../../utils/ActionUtils';
import { indexKeys } from '../../../utils/IndexUtils';
import { forceAmericanDecimalSeparator, formatNumber } from '../../../utils/NumberUtils';
import { initializeModal, isModalOpen } from '../../../utils/ModalUtils';

// Presets do modo Simples: largura da faixa (% em torno do preço atual) e nº de níveis. Só apresentação —
// resolvem os campos crus (limites/níveis) por baixo; o estado editado é o MESMO do modo Avançado.
const WIDTH_PRESETS = [
    { pct: 0.02, label: '🎯 Estreita', hint: '±2%' },
    { pct: 0.05, label: '⚖️ Média', hint: '±5%' },
    { pct: 0.10, label: '🌊 Larga', hint: '±10%' },
];
const LEVEL_PRESETS = [
    { n: 3, label: 'Poucos', hint: '3' },
    { n: 5, label: 'Equilibrado', hint: '5' },
    { n: 10, label: 'Muitos', hint: '10' },
];
const fmtNum = n => (Number(n) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 });

/**
 * props:
 * - data
 * - onSubmit
 */
function GridModal(props) {

    const btnClose = useRef('');
    const btnSave = useRef('');
    const notionalPrice = useRef('');

    const upperLowerMarginInput = useRef('');
    const lowerLimitInput = useRef('');
    const upperLimitInput = useRef('');

    const MODAL_ID = 'modalGrid';

    const DEFAULT_AUTOMATION = {
        conditions: '',
        name: '',
        indexes: '',
        actions: []
    };
    const DEFAULT_GRID = {
        lowerLimit: '',
        upperLimit: '',
        levels: '',
        quantity: ''
    };

    const [gridView, setGridView] = useState(false);
    const [error, setError] = useState('');
    const [automation, setAutomation] = useState(DEFAULT_AUTOMATION);
    const [grid, setGrid] = useState(DEFAULT_GRID);
    // Modo do formulário: 'simple' (padrão, por faixa+níveis prontos) ou 'advanced' (campos crus). Só de
    // apresentação → localStorage. O estado editado (grid) é o mesmo nos dois modos.
    const [formMode, setFormMode] = useState(() => localStorage.getItem('gridFormMode') || 'simple');
    const changeFormMode = m => { setFormMode(m); try { localStorage.setItem('gridFormMode', m); } catch (_) { } };
    const [widthPct, setWidthPct] = useState(null); // só p/ destacar o preset de largura ativo

    function updateNotionalPrice(value) {
        // O campo de Valor Nocional só existe no modo Avançado; no Simples o ref fica vazio → não escreve.
        if (!notionalPrice.current) return;
        // notionalPrice.current.value = `${value}`.substring(0, 10);
        notionalPrice.current.value = formatNumber(value);
    }

    function onGridChange(event) {
        const value = event.target.value === quantityTypeNames.MIN_NOTIONAL ? quantityTypes.MIN_NOTIONAL : forceAmericanDecimalSeparator(event.target.value);
        const fieldName = event.target.id;
        grid[fieldName] = value;

        setGrid(prevState => ({ ...prevState, [fieldName]: value }));

        if (fieldName === 'quantity' && symbol && parseFloat(grid.quantity) < parseFloat(symbol.minLotSize)) {
            setError('Lote mínimo: ' + symbol.minLotSize);
            btnSave.current.disabled = true;
            return;
        }
        else if (fieldName === 'quantity' || fieldName === 'lowerLimit') {
            const notional = grid.lowerLimit * parseFloat(grid.quantity);
            // notionalPrice.current.value = `${notional}`.substring(0, 10);
            updateNotionalPrice(notional);
            if (symbol && notional < parseFloat(symbol.minNotional)) {
                setError(quantityTypeNames.MIN_NOTIONAL + ': ' + symbol.minNotional);
                btnSave.current.disabled = true;
                return;
            }
        }

        btnSave.current.disabled = false;
        setError('');
    }

    const [symbol, setSymbol] = useState(false);
    useEffect(() => {
        if (!automation.symbol) return;

        setError('');
        getSymbol(automation.symbol)
            .then(symbol => {
                if (!symbol) return setSymbol(false); // símbolo não cadastrado (ex.: automação de IA cujo ativo não está na tabela symbols)
                setSymbol(symbol);
                if (grid.quantity === quantityTypeNames.MIN_NOTIONAL)
                    // notionalPrice.current.value = `${symbol.minNotional}`;
                    updateNotionalPrice(symbol.minNotional);
                else
                    // notionalPrice.current.value = `${grid.quantity * grid.lowerLimit}`.substring(0, 10);
                    updateNotionalPrice(grid.quantity * grid.lowerLimit);
            })
            .catch(err => setError(processError(err)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [automation.symbol]);

    const [wallet, setWallet] = useState({
        base: {
            symbol: '',
            qty: 0
        },
        quote: {
            symbol: '',
            qty: 0
        }
    });

    async function loadWallet(symbol) {
        try {
            const baseQty = await getMemoryIndex(symbol.base, indexKeys.WALLET, '');
            const quoteQty = await getMemoryIndex(symbol.quote, indexKeys.WALLET, '');
            setWallet({
                base: { symbol: symbol.base, qty: baseQty },
                quote: { symbol: symbol.quote, qty: quoteQty }
            });
        }
        catch (err) {
            setError(processError(err));
        }
    }

    useEffect(() => {
        if (!symbol || !symbol.base) return;

        loadWallet(symbol);
    }, [symbol]);

    // isVisible: usado pra renderizar apenas quando a modal estiver sendo exibida, para evitar processamento de stream de um dos subcomponentes
    const [isVisible, setIsVisible] = useState(isModalOpen(MODAL_ID));

    useEffect(() => {
        initializeModal(MODAL_ID,
            () => {
                setIsVisible(false);
                setGridView(false);
                setAutomation({ ...DEFAULT_AUTOMATION });
                setGrid({ ...DEFAULT_GRID });
                setWidthPct(null);
            },
            () => {
                setIsVisible(true);
                setGridView(false);
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function onSubmit(event) {
        setError('');
        const quantity = grid.quantity === quantityTypeNames.MIN_NOTIONAL ? quantityTypes.MIN_NOTIONAL : grid.quantity;
        if (!quantity) return setError('Quantidade é obrigatória.');

        // Campos obrigatórios de automation mas que não estão na tela de Grid
        automation.name = `${actionTypes.GRID} ${automation.symbol} #${grid.levels}`;
        automation.actions = [{ type: actionTypes.GRID }];
        const indexName = `${automation.symbol}:${indexKeys.BOOK}`;
        automation.indexes = indexName;
        const variableBestAsk = `MEMORY['${indexName}'].current.bestAsk`;
        const variableBestBid = `MEMORY['${indexName}'].current.bestBid`;
        automation.conditions = `${variableBestAsk}>${grid.lowerLimit} && ${variableBestBid}<${grid.upperLimit}`;

        saveGrid(automation.id, automation, grid.levels, quantity)
            .then(result => {
                btnClose.current.click();
                if (props.onSubmit) props.onSubmit(result);
            })
            .catch(err => setError(processError(err)))
    }

    function onInputChange(event) {
        setAutomation(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    useEffect(() => {
        if (!props.data) return;
        setAutomation(props.data);

        if (!props.data.id) return setGrid(DEFAULT_GRID);

        const conditionSplit = props.data.conditions.split(' && ');
        if (!conditionSplit || conditionSplit.length < 2) return;

        if (!props.data.grids || !props.data.grids.length) return;

        const quantity = props.data.grids[0].orderTemplate.quantity;
        setGrid({
            lowerLimit: parseFloat(conditionSplit[0].split('>')[1]),
            upperLimit: parseFloat(conditionSplit[1].split('<')[1]),
            levels: props.data.grids.length + 1,
            quantity: quantity === quantityTypes.MIN_NOTIONAL ? quantityTypeNames.MIN_NOTIONAL : quantity
        });
        setShowLogs(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data.id]);

    function onViewGridsClick(event) {
        if (!gridView) setShowLogs(false);
        setGridView(!gridView);
    }

    const [showLogs, setShowLogs] = useState(false);
    function onLogClick(event) {
        if (!showLogs) setGridView(false);
        setShowLogs(!showLogs);
    }

    async function getAverageValue() {
        try {
            const book = await getMemoryIndex(automation.symbol, indexKeys.BOOK, '');
            if (!book) return;

            let avgValue = (book.current.bestBid + book.current.bestAsk) / 2;
            avgValue = avgValue.toFixed(0);
            return avgValue;
        }
        catch (err) {
            console.error(err);
        }
    }

    function isNumeric(val) {
        return /^-?\d+$/.test(val);
    }

    async function onChangeUpperLowerMargin(event) {
        const margin = parseInt(upperLowerMarginInput.current.value);
        if (!isNumeric(margin)) return;

        const avgValue = parseInt(await getAverageValue());
        const lowerLimit = avgValue - margin;
        const upperLimit = avgValue + margin;
        setGrid(prevState => ({
            ...prevState,
            lowerLimit,
            upperLimit
        }));
    }

    // Modo Simples: largura da faixa em % do preço atual → resolve limite inferior/superior.
    async function applyWidthPct(pct) {
        const avg = parseInt(await getAverageValue());
        if (!avg) { setError('Preço indisponível agora — tente de novo em instantes.'); return; }
        const margin = Math.max(1, Math.round(avg * pct));
        setGrid(prevState => ({ ...prevState, lowerLimit: avg - margin, upperLimit: avg + margin }));
        setWidthPct(pct);
        setError('');
        if (btnSave.current) btnSave.current.disabled = false;
    }
    function applyLevels(n) { setGrid(prevState => ({ ...prevState, levels: n })); }
    function useMinQuantity() { setGrid(prevState => ({ ...prevState, quantity: quantityTypeNames.MIN_NOTIONAL })); }

    return (
        <div className='modal fade' id={MODAL_ID} tabIndex='-1' role='dialog' aria-labelledby='modalTitleNotify' aria-hidden='true'>
            <div className='modal-dialog modal-dialog-centered' role='document'>
                <div className='modal-content'>
                    <div className="modal-header">
                        <p className="modal-title" id="modalTitleNotify">{props.data.id ? 'Editar ' : 'Nova '}Automação Grid</p>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>

                    <div className="modal-body">
                        <div className="form-group">
                            <div className='row'>
                                <div className='col-md-6 mb-3'>
                                    <label htmlFor='symbol'>Símbolo:</label>
                                    <SelectSymbol onChange={onInputChange} symbol={automation.symbol} disabled={automation.id > 0} />
                                </div>
                                <div className='col-md-6 mb-3'>
                                    {
                                        isVisible && automation.symbol
                                            ? <SymbolPrice symbol={automation.symbol} />
                                            : <React.Fragment></React.Fragment>
                                    }
                                </div>
                            </div>
                        </div>
                        {
                            !gridView && !showLogs
                                ? (
                                    <React.Fragment>
                                        <div className='form-group'>
                                            <div className='row'>
                                                <label>Você possui:</label>
                                                <WalletSummary wallet={wallet} symbol={symbol} />
                                            </div>

                                            {/* Toggle Simples ↔ Avançado — só apresentação (localStorage). Estado editado é o mesmo. */}
                                            <div className='d-flex justify-content-end mb-2'>
                                                <div className='btn-group btn-group-sm' role='group' aria-label='Modo do formulário'>
                                                    <button type='button' className={`btn ${formMode === 'simple' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => changeFormMode('simple')}>Simples</button>
                                                    <button type='button' className={`btn ${formMode === 'advanced' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => changeFormMode('advanced')}>Avançado</button>
                                                </div>
                                            </div>

                                            {formMode === 'simple' ? (
                                                <React.Fragment>
                                                    {/* Largura da faixa (resolve limite inferior/superior em torno do preço atual) */}
                                                    <div className='mb-3'>
                                                        <label className='form-label mb-1'>Largura da faixa <small className='text-muted'>(em torno do preço atual)</small></label>
                                                        <div className='btn-group w-100' role='group'>
                                                            {WIDTH_PRESETS.map(w => (
                                                                <button key={w.pct} type='button'
                                                                    className={`btn btn-sm ${widthPct === w.pct ? 'btn-primary' : 'btn-outline-primary'}`}
                                                                    onClick={() => applyWidthPct(w.pct)}>
                                                                    {w.label} <span className='d-none d-sm-inline'>{w.hint}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                        <small className='text-muted d-block mt-1'>Faixa estreita rende mais em lateral apertada; larga aguenta oscilação maior sem sair da grade.</small>
                                                    </div>

                                                    {/* Quantos níveis */}
                                                    <div className='mb-3'>
                                                        <label className='form-label mb-1'>Quantos níveis</label>
                                                        {automation.id > 0 ? (
                                                            <div><span className='badge bg-secondary'>{grid.levels || '—'} níveis</span> <small className='text-muted'>(não editável após criar)</small></div>
                                                        ) : (
                                                            <div className='btn-group w-100' role='group'>
                                                                {LEVEL_PRESETS.map(l => (
                                                                    <button key={l.n} type='button'
                                                                        className={`btn btn-sm ${Number(grid.levels) === l.n ? 'btn-primary' : 'btn-outline-primary'}`}
                                                                        onClick={() => applyLevels(l.n)}>
                                                                        {l.label} <span className='d-none d-sm-inline'>({l.hint})</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <small className='text-muted d-block mt-1'>Mais níveis = ordens menores e mais frequentes dentro da faixa.</small>
                                                    </div>

                                                    {/* Quantidade por nível */}
                                                    <div className='mb-3'>
                                                        <label className='form-label mb-1'>Quantidade por nível</label>
                                                        <div className='input-group input-group-sm' style={{ maxWidth: 340 }}>
                                                            <input className='form-control' id='quantity' type='text' value={grid.quantity || ''} placeholder={symbol ? symbol.minLotSize : ''} onChange={onGridChange} />
                                                            <span className='input-group-text'>{symbol ? symbol.base : ''}</span>
                                                            <button type='button' className='btn btn-outline-secondary' onClick={useMinQuantity}>Usar mínimo</button>
                                                        </div>
                                                        <small className='text-muted d-block mt-1'>“Usar mínimo” aplica o menor valor permitido pela corretora — bom pra começar pequeno.</small>
                                                    </div>

                                                    {/* Resumo compacto do que os presets resolveram */}
                                                    {(grid.lowerLimit || grid.upperLimit) ? (
                                                        <div className='alert alert-light border py-2 mb-3' style={{ fontSize: 13 }}>
                                                            📊 Resumo: faixa <strong>{fmtNum(grid.lowerLimit)}–{fmtNum(grid.upperLimit)} {symbol ? symbol.quote : ''}</strong>
                                                            {grid.levels ? <> · <strong>{grid.levels}</strong> níveis</> : null}
                                                            {grid.quantity ? <> · {grid.quantity === quantityTypeNames.MIN_NOTIONAL ? 'quantidade mínima' : <>{fmtNum(grid.quantity)} {symbol ? symbol.base : ''} por nível</>}</> : null}.
                                                        </div>
                                                    ) : (
                                                        <small className='text-muted d-block mb-3'>Escolha a largura da faixa pra definir os limites automaticamente.</small>
                                                    )}

                                                    {/* Ativo? */}
                                                    <div className='row'>
                                                        <div className='col-md-6 mb-3'>
                                                            <div className='form-group'>
                                                                <SwitchInput id='isActive' text='Ativo?' onChange={onInputChange} isChecked={automation.isActive} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            ) : (
                                                <React.Fragment>
                                                    <div className='row'>
                                                        <div className='col-md-6 mb-3'>
                                                            <label htmlFor='lowerLimit'>Margem Superior/Inferior:</label>
                                                            <input ref={upperLowerMarginInput} className='form-control' type='number' id='upperLowerMargin' placeholder='0' onChange={onChangeUpperLowerMargin} />
                                                        </div>
                                                    </div>
                                                    <div className='row'>
                                                        <div className='col-md-6 mb-3'>
                                                            <label htmlFor='lowerLimit'>Limite Inferior:</label>
                                                            <input ref={lowerLimitInput} className='form-control' type='number' id='lowerLimit' placeholder='0' value={grid.lowerLimit} onChange={onGridChange} />
                                                        </div>
                                                        <div className='col-md-6 mb-3'>
                                                            <label htmlFor='upperLimit'>Limite Superior:</label>
                                                            <input ref={upperLimitInput} className='form-control' type='number' id='upperLimit' placeholder='0' value={grid.upperLimit} onChange={onGridChange} />
                                                        </div>
                                                    </div>
                                                    <div className='row'>
                                                        <div className='col-md-6 mb-3'>
                                                            <label htmlFor='levels'>Níveis:</label>
                                                            <input className='form-control' type='number' id='levels' placeholder='3' value={grid.levels} onChange={onGridChange} disabled={automation.id > 0} />
                                                        </div>
                                                        <div className="col-md-6 mb-3">
                                                            <div className="form-group">
                                                                <label htmlFor="quantity">Quantidade:</label>
                                                                <input className="form-control" id="quantity" type="text" list="gridQtyList" placeholder={symbol ? symbol.minLotSize : ''} value={grid.quantity || ''} onChange={onGridChange} />
                                                                <datalist id="gridQtyList">
                                                                    <option>{quantityTypeNames.MIN_NOTIONAL}</option>
                                                                </datalist>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='row'>
                                                        <div className='col-md-6 mb-3'>
                                                            <label htmlFor='notionalPrice'>Valor Nocional:</label>
                                                            <input ref={notionalPrice} className='form-control' type='number' id='notionalPrice' placeholder='0' disabled />
                                                        </div>
                                                    </div>
                                                    <div className='row'>
                                                        <div className='col-md-6 mb-3'>
                                                            <div className='form-group'>
                                                                <SwitchInput id='isActive' text='Ativo?' onChange={onInputChange} isChecked={automation.isActive} />
                                                            </div>
                                                        </div>
                                                        <div className='col-md-6 mb-3'>
                                                            <div className='form-group'>
                                                                <SwitchInput id='logs' text='Habilitar Logs?' onChange={onInputChange} isChecked={automation.logs} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            )}
                                        </div>
                                    </React.Fragment>
                                )
                                : <React.Fragment></React.Fragment>
                        }
                        {
                            gridView && !showLogs
                                ? (
                                    <div className='form-group' >

                                        <ul className='nav nav-tabs' id='tabs' role='tablist'>
                                            <li className='nav-link' role='presentation' style={{ padding: '0px' }}>
                                                <button style={{ padding: '5px' }} className='nav-link active' id='chart-tab' data-bs-toggle='tab' data-bs-target='#chartTab' type='button' role='tab'>
                                                    Gráfico
                                                </button>
                                            </li>
                                            <li className='nav-link' role='presentation' style={{ padding: '0px' }}>
                                                <button style={{ padding: '5px' }} className='nav-link' id='table-tab' data-bs-toggle='tab' data-bs-target='#tableTab' type='button' role='tab'>
                                                    Tabela
                                                </button>
                                            </li>
                                        </ul>
                                        <div className='tab-content px-3 mb-3' id='tabContent'>
                                            <div className='tab-pane fade show active pt-3' id='chartTab' role='tabpanel'>
                                                <GridLineChart grids={automation.grids} symbol={automation.symbol} />
                                            </div>
                                            <div className='tab-pane fade pt-3' id='tableTab' role='tabpanel'>
                                                <GridTable data={automation.grids} />
                                            </div>
                                        </div>
                                    </div>
                                )
                                : <React.Fragment></React.Fragment>
                        }
                        {
                            showLogs
                                ? <LogView file={'A_' + automation.id} />
                                : <React.Fragment></React.Fragment>
                        }
                    </div>

                    <div className="modal-footer">
                        <ActionMessage error={error} extraClassName="mt-1 col-9 py-1" />
                        <LogButton id={automation.id} onClick={onLogClick} />
                        <GridButton id={automation.id} onClick={onViewGridsClick} />
                        <button ref={btnSave} type="button" className="btn btn-sm btn-primary" onClick={onSubmit}>
                            <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" /></svg>
                            Salvar
                        </button>
                    </div>

                </div>
            </div>
        </div>
    )
}

export default GridModal;